import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { useOfflineChat } from '../hooks/useOfflineChat';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';
import { setCachedKnowledgeBase, shouldUpdateCache } from '../chatbot/kb-cache';
import { KnowledgeBaseResponse } from '../chatbot/types';

/**
 * Hook to track the visual viewport height on mobile.
 * When the on-screen keyboard opens, the visual viewport shrinks.
 * We use this to adjust the chat panel height so the input stays visible.
 */
function useVisualViewportHeight() {
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const handleResize = () => {
      setHeight(vv.height);
    };

    vv.addEventListener('resize', handleResize);
    vv.addEventListener('scroll', handleResize);
    return () => {
      vv.removeEventListener('resize', handleResize);
      vv.removeEventListener('scroll', handleResize);
    };
  }, []);

  return height;
}

export function ChatBubble() {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [input, setInput] = useState('');
  const { messages, loading, isOnline, sendMessage } = useOfflineChat();
  const isMobile = useMediaQuery(768);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const kbFetchedRef = useRef(false);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [savingId, setSavingId] = useState<string | null>(null);
  const [saveToast, setSaveToast] = useState<string | null>(null);
  const [feedbackGiven, setFeedbackGiven] = useState<Map<string, 'up' | 'down'>>(new Map());
  const visualViewportHeight = useVisualViewportHeight();

  // Voice input (speech-to-text)
  const handleVoiceResult = useCallback((transcript: string) => {
    setInput(transcript);
    sendMessage(transcript);
  }, [sendMessage]);

  const { isSupported: sttSupported, isListening, start: startListening, stop: stopListening } =
    useSpeechRecognition({ onResult: handleVoiceResult });

  // Voice output (text-to-speech)
  const { isSupported: ttsSupported, speak } = useSpeechSynthesis();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // On mobile, scroll input into view when keyboard opens
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    const handleFocus = () => {
      // Small delay to let the keyboard finish opening
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ block: 'nearest' });
      }, 300);
    };

    const el = inputRef.current;
    el?.addEventListener('focus', handleFocus);
    return () => {
      el?.removeEventListener('focus', handleFocus);
    };
  }, [isMobile, isOpen]);

  // Fetch knowledge base on mount (once authenticated)
  useEffect(() => {
    if (!isAuthenticated || kbFetchedRef.current) return;
    kbFetchedRef.current = true;

    apiClient<KnowledgeBaseResponse>('/chatbot/knowledge-base')
      .then((data) => {
        if (shouldUpdateCache(data.version)) {
          setCachedKnowledgeBase(data);
        }
      })
      .catch(() => {
        // Silently fail — use any previously cached data
      });
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return null;
  }

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    setInput('');
    await sendMessage(trimmed);
  };

  const handleSaveToKB = async (msgId: string, botText: string) => {
    // Find the preceding user message
    const msgIndex = messages.findIndex((m) => m.id === msgId);
    const userMsg = messages.slice(0, msgIndex).reverse().find((m) => m.sender === 'user');
    if (!userMsg) return;

    setSavingId(msgId);
    try {
      await apiClient('/knowledge/entries', {
        method: 'POST',
        body: {
          question: userMsg.text,
          answer: botText,
          status: 'PendingReview',
          category: 'General',
        },
      });
      setSavedIds((prev) => new Set([...prev, msgId]));
      setSaveToast('Saved to Knowledge Base for review!');
      setTimeout(() => setSaveToast(null), 3000);
    } catch {
      setSaveToast('Failed to save. Please try again.');
      setTimeout(() => setSaveToast(null), 3000);
    } finally {
      setSavingId(null);
    }
  };

  const handleFeedback = (msgId: string, botText: string, rating: 'up' | 'down') => {
    if (feedbackGiven.has(msgId)) return;
    // Find the preceding user message
    const msgIndex = messages.findIndex((m) => m.id === msgId);
    const userMsg = messages.slice(0, msgIndex).reverse().find((m) => m.sender === 'user');
    const question = userMsg?.text || '';

    setFeedbackGiven((prev) => new Map(prev).set(msgId, rating));
    // Fire and forget
    apiClient('/telemetry/chatbot-feedback', {
      method: 'POST',
      body: { question, answer: botText, rating },
    }).catch(() => {});
  };

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <div style={isMobile ? {
          ...styles.panelMobile,
          // When the keyboard is open, shrink the panel to fit the visual viewport
          ...(visualViewportHeight ? { height: `${visualViewportHeight - 56}px`, bottom: 'auto' } : {}),
        } : isExpanded ? styles.panelExpanded : styles.panel}>
          {/* Header */}
          <div style={styles.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={styles.headerTitle}>KDU Concierge</span>
              <span style={styles.connectivityBadge}>
                {isOnline ? '🟢 Online' : '📴 Offline'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                style={styles.closeBtn}
                onClick={() => setIsExpanded((prev) => !prev)}
                aria-label={isExpanded ? 'Minimize chat' : 'Expand chat'}
                title={isExpanded ? 'Minimize' : 'Expand'}
              >
                {isExpanded ? '⊟' : '⊞'}
              </button>
              <button style={styles.closeBtn} onClick={handleToggle} aria-label="Close chat">
                ✕
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div style={styles.messagesArea}>
            {messages.length === 0 && (
              <div style={styles.emptyState}>
                <span style={{ fontSize: '32px' }}>💬</span>
                <p style={{ margin: '8px 0 0', color: '#666', fontSize: '13px' }}>
                  Ask me about events, venues, tickets, or anything about the reunion!
                </p>
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  ...styles.messageBubble,
                  ...(msg.sender === 'user' ? styles.userMessage : styles.botMessage),
                }}
              >
                <div style={{ fontSize: '13px', lineHeight: '1.4', whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: msg.text.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #1a6b3c; text-decoration: underline;">Open in Google Maps</a>') }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                  {msg.source && msg.sender === 'bot' && (
                    <span style={styles.sourceTag}>
                      {msg.source === 'database' && '📊 From records'}
                      {msg.source === 'faq' && '📋 FAQ'}
                      {msg.source === 'llm' && '🤖 AI'}
                      {msg.source === 'fallback' && ''}
                    </span>
                  )}
                  {msg.offline && msg.sender === 'bot' && (
                    <span style={styles.offlineBadge}>📴 Offline</span>
                  )}
                  {ttsSupported && msg.sender === 'bot' && (
                    <button
                      style={styles.ttsBtn}
                      onClick={() => speak(msg.text.replace(/<[^>]*>/g, ''))}
                      aria-label="Read message aloud"
                    >
                      🔊
                    </button>
                  )}
                  {msg.sender === 'bot' && !savedIds.has(msg.id) && (
                    <button
                      style={styles.saveKbBtn}
                      onClick={() => handleSaveToKB(msg.id, msg.text)}
                      disabled={savingId === msg.id}
                      title="Save to Knowledge Base"
                    >
                      {savingId === msg.id ? '...' : '💾'}
                    </button>
                  )}
                  {msg.sender === 'bot' && savedIds.has(msg.id) && (
                    <span style={{ fontSize: '10px', color: '#2e7d32' }}>✓ Saved</span>
                  )}
                  {msg.sender === 'bot' && !feedbackGiven.has(msg.id) && (
                    <>
                      <button
                        style={styles.ttsBtn}
                        onClick={() => handleFeedback(msg.id, msg.text, 'up')}
                        aria-label="Helpful response"
                        title="Helpful"
                      >
                        👍
                      </button>
                      <button
                        style={styles.ttsBtn}
                        onClick={() => handleFeedback(msg.id, msg.text, 'down')}
                        aria-label="Not helpful response"
                        title="Not helpful"
                      >
                        👎
                      </button>
                    </>
                  )}
                  {msg.sender === 'bot' && feedbackGiven.has(msg.id) && (
                    <span style={{ fontSize: '10px', color: feedbackGiven.get(msg.id) === 'up' ? '#2e7d32' : '#c62828' }}>
                      {feedbackGiven.get(msg.id) === 'up' ? '👍 Thanks!' : '👎 Noted'}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ ...styles.messageBubble, ...styles.botMessage }}>
                <div style={styles.typingDots}>
                  <span style={styles.dot}>●</span>
                  <span style={{ ...styles.dot, animationDelay: '0.2s' }}>●</span>
                  <span style={{ ...styles.dot, animationDelay: '0.4s' }}>●</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form style={isMobile ? styles.inputAreaMobile : styles.inputArea} onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              inputMode="text"
              enterKeyHint="send"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question..."
              style={styles.input}
              disabled={loading}
            />
            {sttSupported && (
              <button
                type="button"
                style={{
                  ...styles.micBtn,
                  backgroundColor: isListening ? '#dc3545' : '#1a6b3c',
                  animation: isListening ? 'voicePulse 1.5s infinite' : 'none',
                }}
                onClick={() => (isListening ? stopListening() : startListening())}
                aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              </button>
            )}
            <button
              type="submit"
              style={{
                ...styles.sendBtn,
                opacity: !input.trim() || loading ? 0.5 : 1,
                cursor: !input.trim() || loading ? 'not-allowed' : 'pointer',
              }}
              disabled={!input.trim() || loading}
              aria-label="Send message"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </form>
        </div>
      )}

      {/* Save to KB Toast */}
      {saveToast && (
        <div style={styles.saveToast}>
          {saveToast}
        </div>
      )}

      {/* Floating Bubble */}
      <button
        style={styles.bubble}
        onClick={handleToggle}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? '✕' : '💬'}
      </button>

      {/* Keyframe animation for typing dots and voice pulse */}
      <style>{`
        @keyframes chatBubblePulse {
          0%, 60%, 100% { opacity: 0.3; }
          30% { opacity: 1; }
        }
        @keyframes voicePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(220, 53, 69, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .voice-pulse-btn { animation: none !important; }
        }
      `}</style>
    </>
  );
}


const styles: Record<string, React.CSSProperties> = {
  bubble: {
    position: 'fixed',
    bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
    right: '24px',
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: '#1a6b3c',
    color: 'white',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    zIndex: 1000,
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  panel: {
    position: 'fixed',
    bottom: '90px',
    right: '24px',
    width: '360px',
    height: '480px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 1000,
  },
  panelExpanded: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '500px',
    height: '75vh',
    maxHeight: 'calc(100vh - 48px)',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 1000,
    transition: 'width 0.2s, height 0.2s',
  },
  panelMobile: {
    position: 'fixed',
    top: '56px',
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: 'calc(100dvh - 56px)',
    backgroundColor: '#ffffff',
    borderRadius: 0,
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 1000,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    backgroundColor: '#1a6b3c',
    color: 'white',
  },
  headerTitle: {
    fontSize: '15px',
    fontWeight: 600,
  },
  connectivityBadge: {
    fontSize: '11px',
    opacity: 0.9,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '8px',
    minWidth: '44px',
    minHeight: '44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    boxSizing: 'border-box',
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    padding: '24px',
  },
  messageBubble: {
    maxWidth: '80%',
    padding: '8px 12px',
    borderRadius: '12px',
    wordBreak: 'break-word',
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#1a6b3c',
    color: 'white',
    borderBottomRightRadius: '4px',
  },
  botMessage: {
    alignSelf: 'flex-start',
    backgroundColor: '#f0f0f0',
    color: '#333',
    borderBottomLeftRadius: '4px',
  },
  sourceTag: {
    fontSize: '10px',
    opacity: 0.7,
  },
  offlineBadge: {
    fontSize: '10px',
    opacity: 0.7,
    backgroundColor: '#fff3cd',
    padding: '1px 4px',
    borderRadius: '4px',
    color: '#856404',
  },
  typingDots: {
    display: 'flex',
    gap: '4px',
    padding: '4px 0',
  },
  dot: {
    fontSize: '12px',
    animation: 'chatBubblePulse 1.2s infinite',
    opacity: 0.3,
  },
  inputArea: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 12px',
    paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
    borderTop: '1px solid #e0e0e0',
    gap: '10px',
    backgroundColor: '#fff',
    flexShrink: 0,
  },
  inputAreaMobile: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
    borderTop: '1px solid #e0e0e0',
    gap: '8px',
    backgroundColor: '#fff',
    flexShrink: 0,
    position: 'sticky' as const,
    bottom: 0,
    zIndex: 10,
  },
  input: {
    flex: 1,
    padding: '10px 14px',
    minHeight: '44px',
    borderRadius: '20px',
    border: '1px solid #ddd',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  sendBtn: {
    width: '48px',
    height: '48px',
    minWidth: '48px',
    minHeight: '48px',
    borderRadius: '50%',
    backgroundColor: '#1a6b3c',
    color: 'white',
    border: 'none',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  saveKbBtn: {
    background: 'none',
    border: 'none',
    fontSize: '12px',
    cursor: 'pointer',
    padding: '2px 4px',
    borderRadius: '4px',
    opacity: 0.7,
    transition: 'opacity 0.15s',
  },
  micBtn: {
    width: '44px',
    height: '44px',
    minWidth: '44px',
    minHeight: '44px',
    borderRadius: '50%',
    color: 'white',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxSizing: 'border-box' as const,
    flexShrink: 0,
    transition: 'background-color 0.2s',
  },
  ttsBtn: {
    background: 'none',
    border: 'none',
    fontSize: '10px',
    cursor: 'pointer',
    padding: '2px 4px',
    borderRadius: '4px',
    opacity: 0.7,
    transition: 'opacity 0.15s',
  },
  saveToast: {
    position: 'fixed',
    bottom: '90px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '0.5rem 1rem',
    backgroundColor: '#333',
    color: '#fff',
    borderRadius: '6px',
    fontSize: '0.8rem',
    zIndex: 1100,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
};
